import {
  countryCodeFromText,
  countryDisplayName,
  shippingAddressFieldIssue,
  shippingAddressSchema,
} from "@loot-os/api-contract";
import { orderAddressCountry } from "@loot-os/fulfillment-core/order/address-country";
import { Button } from "@loot-os/ui/components/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@loot-os/ui/components/dialog";
import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { addressCountryChoices } from "../lib/orderEdits";
import {
  ADDRESS_ISSUE_MESSAGES,
  toShippingAddressDraft,
  withoutBlanks,
} from "../lib/shippingAddressDraft";
import { useTRPC } from "../lib/trpc";
import { ShippingAddressFields } from "./ShippingAddressFields";

/**
 * TCG Fulfill Order's Edit Address: every field, the phone included (Retool's form dropped it on
 * save). The country is free text as in Retool, opening on the order's own country by name, and
 * is sent as the ISO code it names (`countryCodeFromText`); the countries the environment ships to
 * are suggested. The server checks the fields every time and the destination only when the
 * country changes, and keeps the stored address's shape. The tenant that owns the order is told
 * (`address_changed`).
 */
export function OrderAddressDialog({
  orderId,
  address,
  shippingCountries,
  disabledReason,
}: {
  orderId: string;
  address: unknown;
  shippingCountries: readonly string[];
  /** Why Edit Address is off for this order, or undefined when it is on (`orderActionBlock`). */
  disabledReason: string | undefined;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const orderCountry = orderAddressCountry(address);
  const countries = addressCountryChoices(shippingCountries, orderCountry);
  const startDraft = () =>
    toShippingAddressDraft(address, orderCountry ? countryDisplayName(orderCountry) : undefined);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(startDraft);
  const save = useMutation(
    trpc.platform.fulfillment.orders.updateAddress.mutationOptions({
      onSuccess: () => {
        toast.success("Address saved.");
        setOpen(false);
        void queryClient.invalidateQueries({
          queryKey: trpc.platform.fulfillment.orders.pathKey(),
        });
      },
    }),
  );
  const countryCode = countryCodeFromText(draft.country);
  const parsed = shippingAddressSchema.safeParse({
    ...withoutBlanks(draft),
    country: countryCode ?? undefined,
  });
  const issue = draft.country.trim()
    ? countryCode === null
      ? "unknown_country"
      : parsed.success
        ? shippingAddressFieldIssue(parsed.data)
        : null
    : null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) setDraft(startDraft());
      }}
    >
      <DialogTrigger
        render={<Button type="button" variant="outline" size="sm" />}
        disabled={disabledReason !== undefined}
        title={disabledReason}
      >
        Edit Address
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit address</DialogTitle>
          <DialogDescription>
            Every field except the second line and the phone number is required. A new country must
            be one this order's environment ships to.
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-3 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (parsed.success && !issue) {
              save.mutate({ id: orderId, shippingAddress: parsed.data });
            }
          }}
        >
          <ShippingAddressFields
            idPrefix="order-address"
            draft={draft}
            countries={countries}
            countryInput="text"
            onChange={setDraft}
          />
          <p className="text-xs text-muted-foreground sm:col-span-2">
            {issue ? ADDRESS_ISSUE_MESSAGES[issue] : null}
          </p>
          <DialogFooter className="sm:col-span-2">
            <DialogClose render={<Button type="button" variant="outline" />}>Cancel</DialogClose>
            <Button type="submit" disabled={save.isPending || !parsed.success || issue !== null}>
              {save.isPending ? "Saving…" : "Save address"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
